import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { X, Quote, Clock } from "lucide-react";
import INPUT_TYPES from "../constants/input-types";
import { MarkdownRenderer, extractPlainText } from "../utils/markdownRenderer";

const InputBar = ({
    onSend,
    disabled,
    quotedMessage,
    onRemoveQuote,
    onShowRecentContext,
    recentContextCount,
}) => {
    const [input, setInput] = useState("");
    const inputRef = useRef(null);

    /* --- Send Handler --- */
    const handleSend = async (message, input_type) => {
        if (input_type === INPUT_TYPES.TEXT) {
            const trimmed = message.trim();
            if (trimmed && !disabled) {
                const messageData = quotedMessage
                    ? {
                        quotedContent: quotedMessage.message,
                        additionalText: trimmed,
                        isQuoted: true,
                    }
                    : {
                        additionalText: trimmed,
                        isQuoted: false,
                    };
                await onSend(messageData, input_type);
                setInput("");
            }
        } else if (input_type === INPUT_TYPES.AUDIO) {
            await onSend(message, input_type);
            setInput("");
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSend(input, INPUT_TYPES.TEXT);
        }
    };

    return (
        <div className="mx-3 mb-3 sm:mx-4 sm:mb-4" style={{ touchAction: 'manipulation' }}>
            {/* --- Quoted Message Preview --- */}
            {quotedMessage && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-400 rounded-lg p-3 mb-2"
                >
                    <div className="flex items-start justify-between">
                        <div className="flex items-start gap-2 flex-1">
                            <Quote
                                size={16}
                                className="text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0"
                            />
                            <div className="flex-1">
                                <div className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                                    {extractPlainText(quotedMessage.message).length > 80
                                        ? `${extractPlainText(quotedMessage.message).substring(0, 80)}...`
                                        : extractPlainText(quotedMessage.message)}
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Quoting previous message
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onRemoveQuote}
                            aria-label="Remove quoted message"
                            className="ml-2 p-1 hover:bg-blue-100 dark:hover:bg-blue-800 rounded-full transition-colors"
                        >
                            <X size={16} className="text-gray-500 dark:text-gray-400" />
                        </button>
                    </div>
                </motion.div>
            )}

            {/* --- Input Bar --- */}
            <div className="flex items-center gap-2 rounded-full bg-white dark:bg-gray-800 shadow-md dark:shadow-gray-900/40 p-2 transition-colors w-full mx-auto">
                {/* Recent Context Button */}
                {recentContextCount > 0 && (
                    <button
                        onClick={onShowRecentContext}
                        aria-label="Show recent context"
                        className="relative flex-shrink-0 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        <Clock size={18} className="text-blue-500" />
                        {recentContextCount > 0 && (
                            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-[10px] font-semibold text-white">
                                {recentContextCount > 9 ? "9+" : recentContextCount}
                            </span>
                        )}
                    </button>
                )}

                {/* Text Input */}
                <input
                    ref={inputRef}
                    type="text"
                    inputMode="text"
                    autoComplete="off"
                    autoCorrect="on"
                    spellCheck="true"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                        quotedMessage
                            ? "Add your follow-up question..."
                            : "Type a message..."
                    }
                    disabled={disabled}
                    className="flex-grow min-w-0 bg-transparent px-3 sm:px-4 py-2 text-[16px] text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 outline-none disabled:opacity-50 transition-colors"
                />

                {/* Send Button */}
                <motion.button
                    whileHover={{ scale: input.trim() && !disabled ? 1.1 : 1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleSend(input, INPUT_TYPES.TEXT)}
                    disabled={!input.trim() || disabled}
                    aria-label="Send message"
                    className={`flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-full transition-colors duration-300 ${input.trim() && !disabled
                            ? "bg-blue-500 hover:bg-blue-600 text-white dark:bg-blue-600 dark:hover:bg-blue-700"
                            : "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        }`}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 5l7 7-7 7M5 5l7 7-7 7"
                        />
                    </svg>
                </motion.button>
            </div>
        </div>
    );
};
InputBar.propTypes = {
    onSend: PropTypes.func.isRequired,

    disabled: PropTypes.bool,

    quotedMessage: PropTypes.shape({
        message: PropTypes.string,
    }),

    onRemoveQuote: PropTypes.func,

    onShowRecentContext: PropTypes.func,

    recentContextCount: PropTypes.number,
};
export default InputBar;
